import { FileText, Link as LinkIcon, MessageSquare, Paperclip, Plus, Send, Smartphone } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { RecipientPicker } from '@/features/messaging/components/RecipientPicker';
import { cn } from '@/lib/utils';
import type { MessageAudience, MessageChannel } from '@/types/messaging';

/** The server's own limit, mirrored so the counter turns red before a refusal. */
export const MESSAGE_MAX_LENGTH = 480;

interface SendNotificationCardProps {
  selectedChannel: MessageChannel;
  setSelectedChannel: (ch: MessageChannel) => void;
  recipientType: 'ALL' | 'SECTION' | 'INDIVIDUAL';
  setRecipientType: (rt: 'ALL' | 'SECTION' | 'INDIVIDUAL') => void;
  selectedClass: string;
  setSelectedClass: (cls: string) => void;
  selectedSection: string;
  setSelectedSection: (sec: string) => void;
  includeStudents: boolean;
  setIncludeStudents: (v: boolean) => void;
  includeParents: boolean;
  setIncludeParents: (v: boolean) => void;
  includeTeachers: boolean;
  setIncludeTeachers: (v: boolean) => void;
  includeStaff: boolean;
  setIncludeStaff: (v: boolean) => void;
  template: string;
  setTemplate: (tpl: string) => void;
  message: string;
  setMessage: (msg: string) => void;
  isSending: boolean;
  handleSendNotification: () => void;
  /** The roll, the classes and the templates. Null while it is still loading. */
  audience: MessageAudience | null;
  isLoadingAudience: boolean;
  /** Which students are ticked, on a "By individuals" send. */
  chosen: Set<string>;
  onToggleStudent: (uuid: string) => void;
  onSelectAllStudents: (uuids: string[], next: boolean) => void;
}

export function SendNotificationCard({
  selectedChannel,
  setSelectedChannel,
  recipientType,
  setRecipientType,
  selectedClass,
  setSelectedClass,
  selectedSection,
  setSelectedSection,
  includeStudents,
  setIncludeStudents,
  includeParents,
  setIncludeParents,
  includeTeachers,
  setIncludeTeachers,
  includeStaff,
  setIncludeStaff,
  template,
  setTemplate,
  message,
  setMessage,
  isSending,
  handleSendNotification,
  audience,
  isLoadingAudience,
  chosen,
  onToggleStudent,
  onSelectAllStudents,
}: SendNotificationCardProps) {
  const classes = audience?.classes ?? [];
  const sections = classes.find((item) => item.uuid === selectedClass)?.sections ?? [];
  const templates = audience?.templates ?? [];

  /**
   * A channel this installation has no sender for cannot be picked. The button
   * is still drawn — the office should see that WhatsApp exists as an option
   * and ask their administrator for it, rather than wonder where it went.
   */
  const smsReady = audience?.channels.sms ?? false;
  const whatsappReady = audience?.channels.whatsapp ?? false;

  const isOverLimit = message.length > MESSAGE_MAX_LENGTH;
  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-4 sm:p-6 shadow-xs flex flex-col gap-5 w-full max-w-full overflow-hidden">
      <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800/80 pb-2">
        <div className="h-10 w-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
          <Send className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-xl font-extrabold text-slate-700 dark:text-white">
            Send Notification
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
            Broadcast messages via SMS or WhatsApp to students, parents, and teachers.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
        {/* Left Column: Controls */}
        <div className="lg:col-span-5 flex flex-col gap-5">
          {/* Channel Switch */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-extrabold text-slate-700 dark:text-slate-200">
              Channel
            </label>
            <div className="grid grid-cols-2 gap-2.5 p-1 bg-slate-100 dark:bg-[#0b1322] border border-slate-200 dark:border-slate-800 rounded-2xl">
              <button
                type="button"
                onClick={() => setSelectedChannel('SMS')}
                title={smsReady ? undefined : 'SMS is not set up on this installation yet'}
                className={cn(
                  'flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-extrabold transition-all cursor-pointer',
                  selectedChannel === 'SMS'
                    ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-600/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                <Smartphone className="h-4.5 w-4.5" />
                <span>SMS</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedChannel('WHATSAPP')}
                title={whatsappReady ? undefined : 'WhatsApp is not set up on this installation yet'}
                className={cn(
                  'flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-extrabold transition-all cursor-pointer',
                  selectedChannel === 'WHATSAPP'
                    ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md shadow-emerald-600/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                <MessageSquare className="h-4.5 w-4.5" />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>

          {/* Recipients Section */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-extrabold text-slate-700 dark:text-slate-200">
              Recipients
            </label>
            <div className="grid grid-cols-3 gap-1 p-1 bg-slate-100 dark:bg-[#0b1322] border border-slate-200 dark:border-slate-800 rounded-2xl text-xs sm:text-sm font-extrabold">
              <button
                type="button"
                onClick={() => setRecipientType('ALL')}
                className={cn(
                  'py-2 px-1 sm:px-2 rounded-xl transition-all cursor-pointer text-center truncate',
                  recipientType === 'ALL'
                    ? 'bg-white dark:bg-[#111c2e] text-indigo-600 dark:text-indigo-400 shadow-2xs font-extrabold'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                All members
              </button>
              <button
                type="button"
                onClick={() => setRecipientType('SECTION')}
                className={cn(
                  'py-2 px-1 sm:px-2 rounded-xl transition-all cursor-pointer text-center truncate',
                  recipientType === 'SECTION'
                    ? 'bg-white dark:bg-[#111c2e] text-indigo-600 dark:text-indigo-400 shadow-2xs font-extrabold'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                By section
              </button>
              <button
                type="button"
                onClick={() => setRecipientType('INDIVIDUAL')}
                className={cn(
                  'py-2 px-1 sm:px-2 rounded-xl transition-all cursor-pointer text-center truncate',
                  recipientType === 'INDIVIDUAL'
                    ? 'bg-white dark:bg-[#111c2e] text-indigo-600 dark:text-indigo-400 shadow-2xs font-extrabold'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                By individuals
              </button>
            </div>
          </div>

          {/* Class & Section Section Header & Dropdowns */}
          {recipientType === 'SECTION' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Select
                label="Class"
                options={classes.map((item) => ({ value: item.uuid, label: item.name }))}
                placeholder={isLoadingAudience ? 'Loading classes…' : 'Choose a class'}
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                disabled={isLoadingAudience || classes.length === 0}
              />
              <Select
                label="Section"
                options={sections.map((item) => ({ value: item.uuid, label: item.name }))}
                placeholder={selectedClass ? 'Choose a section' : 'Choose a class first'}
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                disabled={!selectedClass || sections.length === 0}
              />
            </div>
          )}

          {/* Choosing students by hand.

              The design had no control here — "By individuals" showed nothing at
              all, so the button could be pressed and nothing could be chosen.
              This is the roll picker the compose popup already uses, dropped
              into that empty slot rather than a second one written for this
              screen (CLAUDE.md §3). */}
          {recipientType === 'INDIVIDUAL' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[14px] font-extrabold text-slate-700 dark:text-slate-200">
                Students
              </label>
              {isLoadingAudience ? (
                <SkeletonLines count={4} />
              ) : (
                <RecipientPicker
                  students={audience?.students ?? []}
                  selected={chosen}
                  onToggle={onToggleStudent}
                  onSelectAll={onSelectAllStudents}
                  disabled={isSending}
                />
              )}
            </div>
          )}

          {/* Include Checkboxes Section */}
          <div className="flex flex-col gap-2">
            <label className="text-[14px] font-extrabold text-slate-700 dark:text-slate-200">
              Include
            </label>
            <div className="flex flex-wrap items-center gap-2.5">
              <label
                className={cn(
                  'flex items-center gap-2 py-1.5 px-3 rounded-xl border text-xs font-extrabold cursor-pointer transition-all',
                  includeStudents
                    ? 'border-indigo-300 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 shadow-2xs'
                    : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-300'
                )}
              >
                <input
                  type="checkbox"
                  checked={includeStudents}
                  onChange={(e) => setIncludeStudents(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                />
                <span className="text-[14px]">Students</span>
              </label>

              <label
                className={cn(
                  'flex items-center gap-2 py-1.5 px-3 rounded-xl border text-xs font-extrabold cursor-pointer transition-all',
                  includeParents
                    ? 'border-indigo-300 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 shadow-2xs'
                    : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-300'
                )}
              >
                <input
                  type="checkbox"
                  checked={includeParents}
                  onChange={(e) => setIncludeParents(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                />
                <span className="text-[14px]">Parents</span>
              </label>

              {/* Teachers and staff are not part of a hand-picked list of
                  students, and staff are not part of a section — the server
                  refuses both rather than quietly widening a send, so the
                  toggles are disabled here instead of failing on submit. */}
              <label
                className={cn(
                  'flex items-center gap-2 py-1.5 px-3 rounded-xl border text-xs font-extrabold transition-all',
                  recipientType === 'INDIVIDUAL'
                    ? 'cursor-not-allowed opacity-50 border-slate-300 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-300'
                    : 'cursor-pointer ' +
                      (includeTeachers
                        ? 'border-indigo-300 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 shadow-2xs'
                        : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-300')
                )}
                title={
                  recipientType === 'INDIVIDUAL'
                    ? 'Choosing individual students cannot also include teachers'
                    : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={includeTeachers}
                  disabled={recipientType === 'INDIVIDUAL'}
                  onChange={(e) => setIncludeTeachers(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                />
                <span className="text-[14px]">Teachers</span>
              </label>

              <label
                className={cn(
                  'flex items-center gap-2 py-1.5 px-3 rounded-xl border text-xs font-extrabold transition-all',
                  recipientType !== 'ALL'
                    ? 'cursor-not-allowed opacity-50 border-slate-300 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-300'
                    : 'cursor-pointer ' +
                      (includeStaff
                        ? 'border-indigo-300 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 shadow-2xs'
                        : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-300')
                )}
                title={
                  recipientType !== 'ALL'
                    ? 'Non-teaching staff belong to the institute, not to a section'
                    : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={includeStaff}
                  disabled={recipientType !== 'ALL'}
                  onChange={(e) => setIncludeStaff(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                />
                <span className="text-[14px]">Staff</span>
              </label>
            </div>

            {/* How many this would actually reach, read from the roll before a
                word is typed — "Send" should never be a guess about who is
                listening. */}
            <p className="text-[12px] font-bold text-slate-500 dark:text-slate-400 mt-0.5">
              {isLoadingAudience
                ? 'Counting who can be reached…'
                : recipientType === 'INDIVIDUAL'
                  ? `${chosen.size} ${chosen.size === 1 ? 'student' : 'students'} chosen`
                  : `${audience?.reachable ?? 0} can be reached${
                      audience?.unreachable
                        ? `, ${audience.unreachable} have no mobile number on file`
                        : ''
                    }`}
            </p>
          </div>
        </div>

        {/* Right Column: Template & Textarea */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 w-full">
            <div className="flex-1 w-full min-w-0">
              <Select
                label="Message template"
                hideLabel
                className="py-2 sm:py-2.5 px-3.5 sm:px-4 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0b1322]"
                options={[
                  { value: '', label: 'Message template (Optional)' },
                  ...templates.map((item) => ({ value: item.key, label: item.name })),
                ]}
                value={template}
                disabled={isLoadingAudience}
                onChange={(e) => {
                  const key = e.target.value;
                  setTemplate(key);

                  // Picking a template fills the box with its opening text; the
                  // office edits it before sending. Clearing the dropdown leaves
                  // whatever they have written alone — wiping a typed message
                  // because a dropdown was reset is not a recoverable mistake.
                  const picked = templates.find((item) => item.key === key);
                  if (picked) setMessage(picked.body);
                }}
              />
            </div>

            <Button
              variant="secondary"
              size="md"
              disabled
              title="Saving your own templates is not available yet"
              className="shrink-0 text-[13px] sm:text-[14px] font-extrabold border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0b1322] text-slate-800 dark:text-slate-100 hover:bg-slate-50 shadow-2xs cursor-pointer py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <Plus className="h-4.5 w-4.5" />
              <span>New template</span>
            </Button>
          </div>

          <div className="flex flex-col rounded-2xl border border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-[#0b1322]/60 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 transition-all p-4 gap-3">
            <textarea
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              aria-label="Notification message"
              className="w-full bg-transparent border-0 text-base text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none resize-none leading-relaxed"
            />

            <div className="flex flex-wrap items-center justify-between gap-2 border border-indigo-200/80 dark:border-indigo-900/60 bg-indigo-50/60 dark:bg-indigo-950/40 p-2.5 rounded-xl shadow-2xs">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled
                  title="Inserting a variable is not available yet"
                  className="p-1.5 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-indigo-900/80 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 transition-colors cursor-pointer rounded-lg shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileText className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled
                  title="Inserting a link is not available yet"
                  className="p-1.5 text-blue-600 dark:text-blue-400 bg-white dark:bg-blue-900/80 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 transition-colors cursor-pointer rounded-lg shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LinkIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled
                  title="Attachments are not available yet"
                  className="p-1.5 text-purple-600 dark:text-purple-400 bg-white dark:bg-purple-900/80 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 transition-colors cursor-pointer rounded-lg shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
              </div>

              {/* A GSM-7 SMS is 160 characters alone and 153 per part once it is
                  concatenated — the same arithmetic the server records against
                  the send, so the two can never disagree. */}
              <span
                className={cn(
                  'font-extrabold text-xs',
                  isOverLimit ? 'text-rose-600 dark:text-rose-400' : 'text-indigo-900 dark:text-indigo-200',
                )}
              >
                {selectedChannel === 'SMS' ? 'SMS' : 'WhatsApp'}:{' '}
                {message.length <= 160 ? 1 : Math.ceil(message.length / 153)} &nbsp; Characters:{' '}
                {message.length}/{MESSAGE_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button
              variant="primary"
              size="md"
              onClick={handleSendNotification}
              isLoading={isSending}
              disabled={isSending || isLoadingAudience || isOverLimit}
              className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-black px-7 py-3 text-[14px] rounded-xl shadow-md shadow-indigo-600/25 flex items-center gap-2 cursor-pointer w-full sm:w-auto justify-center"
            >
              <span>Send Notification</span>
              <Send className="h-4.5 w-4.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
